import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { apiMain } from "@/common/services";
import { IStreamateCamera } from "@/common/services/types";
import { getCameras } from "@/common/utils/streamate";
import { mainBus } from "@/common/buses";
import { useDeviceType } from "@/common/hooks";
import { deviceTypeEnum } from "@/common/utils/device";

interface IProps {
  tags: string[];
}

const DESKTOP_PARAM = "1-2";
const MOBILE_PARAM = "1-5";

export const useStreamateCamera = ({ tags }: IProps) => {
  const [isSound, setSound] = useState(false);
  const [camera, setCamera] = useState<IStreamateCamera | null>(null);
  const [isError, setError] = useState<boolean>(false);

  const isDesktop = useDeviceType() === deviceTypeEnum.DESKTOP;

  const videoRef = useRef<HTMLVideoElement>(null);

  const handleClick = useCallback(() => {
    if (camera === null) {
      console.debug("Camera not loaded, cannot handle click.");
      return;
    }

    if (isSound) {
      setSound(false);
    }

    const url = new URL(camera.url);
    url.searchParams.set("AFNO", isDesktop ? DESKTOP_PARAM : MOBILE_PARAM);

    console.debug(`Opening camera ${camera.nickname} in new tab, url=${url.toString()}`);

    window.open(url, "_blank");
  }, [camera, isSound, isDesktop]);

  const fetchCamera = useMemo(() => async () => {
    try {
      console.debug("Fetching Streamate camera...");

      const response = await apiMain.getStreamateCamera({
        tags,
        exclude: getCameras(),
      });

      console.debug(`Loaded Streamate camera ${response.nickname}.`);
      mainBus.emit("add_streamate_camera", response.id);

      setCamera(response);
    } catch (e) {
      console.error("Error fetching camera:", e);
      setError(true);
    }
  }, []);

  const handleToggleSound = useCallback(() => {
    setSound(prev => !prev);
  }, []);

  useEffect(() => {
    fetchCamera();
  }, []);

  return { camera, handleClick, isError, handleToggleSound, isSound, videoRef };
};
